import React, { useCallback, useContext, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Form } from 'react-final-form';
import { Link as RouterLink } from 'react-router-dom';
import { Box } from '@mui/material';
import AdminFields from 'components/admin/AdminFields';
import PrimaryLoadingButton from 'components/shared/buttons/PrimaryLoadingButton';
import SecondaryButtonLoading from 'components/shared/buttons/SecondaryLoadingButton';
import SEOPageTitle from 'components/shared/SEOPageTitle';
import validateCreateAdminForm from 'common/validations/form/createAdmin';
import { createAdminInitialValues } from 'common/form/initial-values/create';
import { generatePassword } from 'common/form/utils';
import getDataToSave from 'common/form/dataToSave';
import requestStatus from 'common/constants/status';
import routes from 'common/routes';
import AppBarContext from 'context/AppBarContext';
import adminSlice from 'store/modules/admin';

const requiredFields = ['first_name', 'last_name', 'email', 'birthdate', 'ui_language', 'password'];

function AdminCreate() {
  const dispatch = useDispatch();
  const { t } = useTranslation(['buttons', 'routes']);
  const pageTitle = t('administrator.create', { ns: 'routes' });
  const formRef = useRef(null);
  const status = useSelector((state) => state.admin.status.general);
  const { setAppBarButton, setAppBarTitle } = useContext(AppBarContext);

  useEffect(() => {
    if (status === requestStatus.success) {
      formRef.current.restart();
    }
  }, [status]);

  useEffect(() => {
    setAppBarButton(null);
    setAppBarTitle(pageTitle);
  }, [pageTitle, setAppBarButton, setAppBarTitle]);

  const onCreate = useCallback(
    (values) => {
      const dataToSave = getDataToSave(values, 'user');
      dispatch(adminSlice.actions.create(dataToSave));
    },
    [dispatch]
  );

  return (
    <>
      <SEOPageTitle title={pageTitle} />
      <Form
        initialValues={createAdminInitialValues}
        mutators={{
          generatePassword
        }}
        onSubmit={onCreate}
        validate={validateCreateAdminForm}
        render={({ form, handleSubmit, invalid, pristine }) => {
          formRef.current = form;

          return (
            <form onSubmit={handleSubmit}>
              <AdminFields form={form} requiredFields={requiredFields} />
              <Box sx={{ marginTop: '60px', textAlign: 'right' }}>
                <SecondaryButtonLoading
                  component={RouterLink}
                  disabled={status === requestStatus.loading}
                  style={{ marginRight: '32px' }}
                  label={t('cancel', { ns: 'buttons' })}
                  to={routes.listAdmin.path}
                  type="button"
                />
                <PrimaryLoadingButton
                  disabled={invalid || pristine || status === requestStatus.loading}
                  loading={status === requestStatus.loading}
                  label={t('administrator.create', { ns: 'buttons' })}
                  type="submit"
                />
              </Box>
            </form>
          );
        }}
      />
    </>
  );
}

export default AdminCreate;
